import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
// Import Radix UI icons (same as NormalizerNode)
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { TbMultiplier2X } from "react-icons/tb";
import { GrConfigure } from 'react-icons/gr';
import './ScalerNode.css'; // Import the CSS file
import ScalerConfigModal from './ScalerConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';

const isUnspecifiedScalerValue = (value, sentinelLabel) => {
  if (value === undefined || value === null) return true;
  const normalized = String(value).trim();
  return normalized === '' || normalized.toLowerCase() === sentinelLabel.toLowerCase();
};

const ScalerNode = ({ id, data, selected }) => {
  const { multipleFactor, colName, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addScalerNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`ScalerNode ${id} received data:`, data);
    // Log specific column name information for debugging
    console.log(`ScalerNode ${id} column name details:`, {
      colNameFromProps: colName,
      colNameFromData: data.colName,
      colNameType: typeof data.colName,
      isEmpty: data.colName === '',
      isUndefined: data.colName === undefined
    });
  }, [id, data, colName]);
  
  // Check if node is configured properly
  // With auto-select, colName may be blank; treat multipleFactor + name as sufficient
  const hasValidMultipleFactor =
    multipleFactor !== undefined && multipleFactor !== null && multipleFactor !== '';
  const isConfigured = data.isConfigured || (!!name && hasValidMultipleFactor);
  
  // Check if the node needs reconfiguration
  const needsReconfiguration = data.isApiConfigured === false || data.isConfigured === false;
  
  // Log node state for debugging
  useEffect(() => {
    console.log(`ScalerNode ${id} state:`, {
      isConfigured,
      needsReconfiguration,
      name,
      multipleFactor,
      colName,
      dataIsConfigured: data.isConfigured,
      dataIsApiConfigured: data.isApiConfigured
    });
  }, [id, isConfigured, needsReconfiguration, name, multipleFactor, colName, data]);

  // Check if this node has an input connection
  const hasInputConnection = edges.some(edge => edge.target === id);
  
  // Function to handle reconfiguration of the input node
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node name
      const inputNodeName = getInputNodeName();
      
      if (!inputNodeName) {
        throw new Error('No input node connected');
      }
      
      console.log(`Reconfiguring node ${name} with input node ${inputNodeName}`);
      
      // Call the API to update the input node
      await reconfigureNode(
        session.pipeline_name,
        name,
        inputNodeName
      );
      
      console.log(`Node ${name} input reconfigured successfully`);
      
      // Update the node data to mark it as configured
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                isConfigured: true,
                isApiConfigured: true
              }
            };
          }
          return node;
        })
      );
      
      // Also update the session state
      const position = getNodePosition();
      
      addScalerNode(
        name,
        multipleFactor,
        colName,
        inputNodeName,
        { x: position.x, y: position.y }
      );
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error reconfiguring node input:', error);
      notifyError(error, { operation: OP.node.reconfigure });
      setIsConfiguring(false);
    }
  };

  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node name if there's a connection
  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find(edge => edge.target === id);
    if (!inputEdge) return null;
    
    // Get the source node ID
    const sourceNodeId = inputEdge.source;
    
    // Find the actual source node in the nodes array
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    if (!sourceNode) return sourceNodeId;
    
    // If the source node has a name in its data, use that
    if (sourceNode.data && sourceNode.data.name) {
      console.log(`Using source node name from data: ${sourceNode.data.name}`);
      return sourceNode.data.name;
    }
    
    // Get the node mapping
    const nodeMapping = getNodeMapping();
    
    // Get the actual node name from the mapping, or fall back to the ID
    const actualNodeName = nodeMapping[sourceNodeId] || sourceNodeId;
    
    console.log(`ScalerNode ${id} input connection:`, {
      sourceNodeId,
      sourceNodeData: sourceNode.data,
      nodeMapping,
      actualNodeName
    });
    
    return actualNodeName;
  }, [edges, id, nodes, getNodeMapping]);

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    // Log the current state for debugging
    console.log("openConfigModal called with state:", {
      hasInputConnection,
      isConfigured,
      needsReconfiguration,
      multipleFactor,
      colName,
      isModalOpen
    });
    
    if (!hasInputConnection && !isConfigured) {
      // Show an alert if there's no input connection and we're not already configured
      // This allows editing an existing node even if the input is temporarily disconnected
      notifyError(new Error("Please connect an input to this scaler node before configuring it."), {
        toastId: 'scaler.connectInput',
      });
      return;
    }
    
    // Open the full configuration modal
    console.log(`Opening modal for ${needsReconfiguration ? 'reconfiguration' : isConfigured ? 'editing' : 'configuration'}`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setIsModalOpen(true);
      console.log("Modal should be open now:", { isModalOpen: true });
    }, 50);
  }, [hasInputConnection, isConfigured, needsReconfiguration, multipleFactor, colName, isModalOpen]);

  const closeConfigModal = useCallback(() => {
    console.log("Closing modal");
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, multipleFactor, colName } = configData;
      const inputNodeName = getInputNodeName();
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring scaler node ${id} -> ${name}:`, {
        configData,
        inputNodeName,
        position,
        edges: edges.filter(edge => edge.target === id || edge.source === id),
        isConfigured: data.isConfigured,
        isApiConfigured: data.isApiConfigured,
        needsReconfiguration
      });
      
      // Add the scaler node to the session state
      if (inputNodeName) {
        // First update the node ID to name mapping before adding to session
        // This ensures the correct name is used in the pipeline state
        updateNodeMapping(id, name);
        console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
        
        // Find the source node in the nodes array to get its actual name
        const inputEdge = edges.find(edge => edge.target === id);
        const sourceNodeId = inputEdge ? inputEdge.source : null;
        const sourceNode = sourceNodeId ? nodes.find(node => node.id === sourceNodeId) : null;
        
        // Determine the actual input node name to use
        let actualInputNodeName = inputNodeName;
        
        // If the source node has a name in its data, use that
        if (sourceNode && sourceNode.data && sourceNode.data.name) {
          actualInputNodeName = sourceNode.data.name;
          console.log(`Using source node name from data: ${actualInputNodeName}`);
        }
        
        // Check if this is a reconfiguration (node already exists but input changed)
        // Only consider it a reconfiguration if needsReconfiguration is true
        // This prevents treating regular edits as reconfigurations
        const isReconfiguration = needsReconfiguration;
        
        console.log(`${isReconfiguration ? 'Reconfiguring' : 'Adding'} scaler node to session:`, {
          name,
          multipleFactor,
          colName,
          inputNodeName: actualInputNodeName,
          position,
          isReconfiguration
        });
        
        // Check if this is a first-time configuration, a reconfiguration, or an edit
        const isFirstTimeConfig = !data.isConfigured && !data.isApiConfigured;
        const isEditingExistingNode = data.isConfigured && !needsReconfiguration;
        
        console.log('Configuration type:', {
          isFirstTimeConfig,
          isReconfiguration,
          isEditingExistingNode,
          isConfigured: data.isConfigured,
          isApiConfigured: data.isApiConfigured,
          needsReconfiguration
        });
        
        // For first-time configuration, the modal component has already added the node to the pipeline
        if (isFirstTimeConfig && session.pipeline_name) {
          console.log(`First-time configuration for node ${name} with input ${actualInputNodeName}`);
          // No need to call the API here, it was already called by the modal component
        }
        // If this is a reconfiguration, use the reconfigure API
        else if (isReconfiguration && session.pipeline_name) {
          console.log(`Calling reconfigure API for node ${name} with new source ${actualInputNodeName}`);
          
          try {
            console.log(`Reconfiguring node ${name} with new input:`, {
              oldInput: data.inputNodeName,
              newInput: actualInputNodeName
            });
            
            // Call the API to reconfigure the node
            await reconfigureNode(
              session.pipeline_name,
              name,
              actualInputNodeName
            );
            
            console.log(`Node ${name} reconfigured successfully with new input ${actualInputNodeName}`);
          } catch (reconfigError) {
            console.error('Error reconfiguring node:', reconfigError);
            // Don't continue with local updates if API call fails
            throw reconfigError; // Re-throw the error to be caught by the outer try-catch
          }
        }
        // If this is an edit of an existing node, we don't need to call any API here
        // The editScalerNode API was already called by the ScalerConfigModal component
        else if (isEditingExistingNode) {
          console.log(`Editing existing node ${name} - API call already handled by modal`);
        }
        
        // Now update the node in the session/editor immediately so UI reflects new value without refresh
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  multipleFactor,
                  colName,
                  isConfigured: true,
                  isApiConfigured: true,
                  inputNodeName: actualInputNodeName,
                },
              };
            }
            return node;
          })
        );
        // Also update session store so side panels reflect instantly
        addScalerNode(
          name,
          multipleFactor,
          colName,
          actualInputNodeName,
          { x: position.x, y: position.y }
        );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  multipleFactor,
                  colName,
                  isConfigured: true,
                  isApiConfigured: true,
                  inputNodeName: actualInputNodeName
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        console.log(`Updated node mapping in local mode: ${id} -> ${name}`);
        
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  multipleFactor,
                  colName,
                  isConfigured: true,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        
        // Update selection if needed
        if (session.selected_node === id) {
          updateSelectedNode(name);
        }
      }
    } catch (error) {
      console.error('Error configuring scaler node:', error);
      notifyError(error, { operation: needsReconfiguration ? OP.node.reconfigure : OP.node.editScaler });
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getInputNodeName, getNodePosition, closeConfigModal, updateNodeMapping, addScalerNode, session, updateSelectedNode, edges, nodes, data, needsReconfiguration]);

  return (
    <>
      <div
        className={`scaler-node ${selected ? 'scaler-selected' : needsReconfiguration ? 'scaler-needs-reconfiguration' : isConfigured ? 'scaler-configured' : 'scaler-not-configured'}`}
      >
        <div className={`scaler-node-header ${needsReconfiguration ? 'scaler-needs-reconfiguration' : isConfigured ? 'scaler-configured' : 'scaler-not-configured'}`}>
          <div className="scaler-node-header-title">
            <div className="scaler-node-type">Scaler</div>
            {name && (isConfigured || needsReconfiguration) && <div className="scaler-node-name-display">{name}</div>}
          </div>
          <div className="scaler-node-header-icons">
            {/* Show different buttons based on node state */}
            {needsReconfiguration ? (
              <button 
                onClick={handleReconfigureInput}
                className="scaler-config-button scaler-reconfigure-button"
                title="Reconfigure input node"
                disabled={!hasInputConnection || isConfiguring}
              >
                <GrConfigure className="scaler-icon" />
              </button>
            ) : (
              <button 
                onClick={openConfigModal}
                className="scaler-config-button"
                title={isConfigured ? "Edit Configuration" : hasInputConnection ? "Configure Scaler" : "Connect an input first"}
                disabled={(!hasInputConnection && !isConfigured) || isConfiguring}
              >
                {isConfigured ? (
                  <Pencil1Icon className="scaler-icon" />
                ) : (
                  <ExclamationTriangleIcon className="scaler-icon" />
                )}
              </button>
            )}
            

            
            <TbMultiplier2X className="scaler-icon" />
            
            {/* Debug button - only visible in development */}
            {/* Debug button removed to avoid confusion */}
          </div>
        </div>
        <div className="scaler-node-content">
          <div className="scaler-text-xs">
            {!hasInputConnection && (
              <div className="scaler-text-warning">
                Connect an input to configure
              </div>
            )}
            {hasInputConnection && !isConfigured && !needsReconfiguration && (
              <div className="scaler-text-warning">
                Parameters not configured
              </div>
            )}
            {hasInputConnection && needsReconfiguration && (
              <div className="scaler-text-warning" style={{ color: '#ff6b00' }}>
                Needs reconfiguration with new input
              </div>
            )}
            {isConfiguring && (
              <div className="scaler-text-info">
                Configuring...
              </div>
            )}
            {(isConfigured || needsReconfiguration) && (
              <>
                <div className="scaler-param-row">
                  <span>Multiple Factor: </span>
                  <span>{
                    // Check if multipleFactor exists and is not undefined
                    multipleFactor !== undefined && multipleFactor !== '' ? 
                      multipleFactor : 
                      // If not, check if data.multipleFactor exists and is not undefined
                      (data.multipleFactor !== undefined && data.multipleFactor !== '' ? 
                        data.multipleFactor : 
                        'Auto')
                  }</span>
                </div>
                <div className="scaler-param-row">
                  <span>Column: </span>
                  <span>{
                    // Check if colName exists and is not sentinel/blank
                    !isUnspecifiedScalerValue(colName, 'Column name not specified') ? 
                      colName : 
                      // If not, check if data.colName exists and is not sentinel/blank
                      (!isUnspecifiedScalerValue(data.colName, 'Column name not specified') ? 
                        data.colName : 
                        'Auto')
                  }</span>
                </div>
              </>
            )}
          </div>
          <div className="scaler-flex-container">
            <Handle
              type="target"
              position={Position.Left}
              id="input"
              className={`scaler-connection-point ${needsReconfiguration ? 'scaler-needs-reconfiguration' : isConfigured ? 'scaler-configured' : 'scaler-not-configured'}`}
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`scaler-connection-point ${needsReconfiguration ? 'scaler-needs-reconfiguration-output' : isConfigured ? 'scaler-configured-output' : 'scaler-not-configured'}`}
              isConnectable={isConfigured && !needsReconfiguration} // Only allow connections if the node is configured and doesn't need reconfiguration
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
          </div>
        </div>
      </div>

      <ScalerConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        isReconfiguration={needsReconfiguration}
        initialData={{ 
          name: name || '', 
          multipleFactor: multipleFactor ?? data.multipleFactor ?? '',
          colName: colName || data.colName || '',
          inputNodeName: getInputNodeName(),
          position: getNodePosition(),
          isConfigured: isConfigured
        }}
      />
    </>
  );
};

export default ScalerNode;